import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { messageFromError } from '../utils/apiError';   // Block 233

// K1 — the household pantry. Fed by ticking SHARED shopping items and by manual
// add here. Used-up items stay visible but muted, so a repeat purchase revives
// them rather than surprising the user with a "new" item.

const FONT = "'Raleway', sans-serif";
const NAVY = '#1a2e3b';
const PRESET_CATEGORIES = ['Fruit & Veg', 'Meat & Fish', 'Deli', 'Dairy', 'Bakery', 'Frozen', 'Cupboard', 'Drinks', 'Household', 'Other'];
const CAT_EMOJI = {
  'Fruit & Veg': '🥦', 'Meat & Fish': '🥩', 'Deli': '🧀', 'Dairy': '🥛',
  'Bakery': '🍞', 'Frozen': '🧊', 'Cupboard': '🥫', 'Drinks': '🧃', 'Household': '🏠', 'Other': '📦',
};

export default function Pantry() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [name, setName]     = useState('');
  const [qty, setQty]       = useState('');
  const [cat, setCat]       = useState('Cupboard');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/api/pantry');
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) { setError(messageFromError(err, 'Could not load your pantry.')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAdd(e) {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setAdding(true); setError('');
    try {
      const res = await api('/api/pantry', { method: 'POST', body: JSON.stringify({ name: n, quantity: qty.trim() || null, category: cat }) });
      if (!res.ok) throw new Error();
      setName(''); setQty('');
      await load();
    } catch (err) { setError(messageFromError(err, 'Could not add that item.')); }
    finally { setAdding(false); }
  }

  async function patch(item, body) {
    // optimistic
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...body } : i));
    try {
      const res = await api(`/api/pantry/${item.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
    } catch (err) { setError(messageFromError(err, 'Could not update that item.')); load(); }
  }

  async function remove(item) {
    setItems(prev => prev.filter(i => i.id !== item.id));
    try { await api(`/api/pantry/${item.id}`, { method: 'DELETE' }); }
    catch { setError('Delete failed.'); load(); }
  }

  // Group by category; within a category, in-stock/low first, used-up last.
  const grouped = {};
  for (const it of items) {
    const c = it.category || 'Other';
    (grouped[c] = grouped[c] || []).push(it);
  }
  const rank = s => (s === 'used_up' ? 2 : s === 'low' ? 1 : 0);
  Object.values(grouped).forEach(list => list.sort((a, b) => rank(a.status) - rank(b.status) || a.name.localeCompare(b.name)));
  const cats = Object.keys(grouped).sort();

  return (
    <div>
      {/* Manual add */}
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Add to pantry…"
          style={{ flex: '1 1 140px', minWidth: 0, fontFamily: FONT, fontSize: '0.9rem', padding: '0.5rem 0.7rem', border: '1px solid #dde3ea', borderRadius: 8, outline: 'none' }} />
        <input value={qty} onChange={e => setQty(e.target.value)} placeholder="Qty"
          style={{ width: 64, fontFamily: FONT, fontSize: '0.9rem', padding: '0.5rem 0.6rem', border: '1px solid #dde3ea', borderRadius: 8, outline: 'none' }} />
        <select value={cat} onChange={e => setCat(e.target.value)}
          style={{ fontFamily: FONT, fontSize: '0.9rem', padding: '0.5rem', border: '1px solid #dde3ea', borderRadius: 8 }}>
          {PRESET_CATEGORIES.map(c => <option key={c} value={c}>{CAT_EMOJI[c] || '📦'} {c}</option>)}
        </select>
        <button type="submit" disabled={adding || !name.trim()}
          style={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.9rem', padding: '0.5rem 0.9rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, cursor: adding ? 'default' : 'pointer', opacity: adding || !name.trim() ? 0.6 : 1 }}>
          {adding ? '…' : 'Add'}
        </button>
      </form>

      {error && <div style={{ color: '#b91c1c', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{error}</div>}

      {loading ? (
        <p style={{ color: '#8a99a8', fontFamily: FONT }}>Loading your pantry…</p>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#8a99a8', fontFamily: FONT, padding: '2rem 1rem', lineHeight: 1.6 }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🥫</div>
          Your pantry is empty. Tick items off a <strong>shared</strong> shopping list and they land here automatically, or add something above.
        </div>
      ) : (
        cats.map(c => (
          <div key={c} style={{ marginBottom: '1.1rem' }}>
            <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.78rem', letterSpacing: 0.4, textTransform: 'uppercase', color: '#8a99a8', margin: '0 0 0.4rem' }}>
              {CAT_EMOJI[c] || '📦'} {c}
            </div>
            {grouped[c].map(it => {
              const usedUp = it.status === 'used_up';
              const low = it.status === 'low';
              return (
                <div key={it.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem',
                  border: '1px solid #eef2f6', borderRadius: 8, marginBottom: '0.35rem',
                  opacity: usedUp ? 0.45 : 1, background: usedUp ? '#f8fafc' : '#fff',
                }}>
                  <span style={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: '0.92rem', color: NAVY, textDecoration: usedUp ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.name}{it.quantity ? <span style={{ color: '#8a99a8', fontWeight: 400 }}> · {it.quantity}</span> : null}
                  </span>
                  {low && !usedUp && <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#b45309', background: '#fef3c7', padding: '0.1rem 0.4rem', borderRadius: 6 }}>LOW</span>}
                  {usedUp ? (
                    <button onClick={() => patch(it, { status: 'in_stock' })} title="Back in stock"
                      style={pillBtn('#065f46', '#d1fae5')}>Restock</button>
                  ) : (
                    <>
                      <button onClick={() => patch(it, { status: low ? 'in_stock' : 'low' })} title={low ? 'Mark in stock' : 'Mark running low'}
                        style={pillBtn('#b45309', '#fef3c7')}>{low ? 'In stock' : 'Low'}</button>
                      <button onClick={() => patch(it, { status: 'used_up' })} title="Mark used up"
                        style={pillBtn('#4b5563', '#f1f5f9')}>Used up</button>
                    </>
                  )}
                  <button onClick={() => remove(it)} title="Remove from pantry" aria-label="Remove"
                    style={{ border: 'none', background: 'none', color: '#c0cad4', cursor: 'pointer', fontSize: '1rem', padding: '0 0.15rem' }}>×</button>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}

function pillBtn(color, bg) {
  return {
    fontFamily: FONT, fontSize: '0.72rem', fontWeight: 700, color, background: bg,
    border: 'none', borderRadius: 6, padding: '0.25rem 0.5rem', cursor: 'pointer', flexShrink: 0,
  };
}
